export const WIZARD_STEPS = [
  "Select Trip",
  "Traveler Details",
  "Trip Customization",
  "Review & Payment",
  "Confirm Booking",
];

export const SPECIAL_DETAILS_MAX_LENGTH = 4000;

export const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

export const createEmptyTraveler = (isPrimary = false, expanded = false) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  isPrimary,
  first_name: "",
  middle_name: "",
  last_name: "",
  dateOfBirth: null,
  gender: "",
  nationality: "",
  address: "",
  country: "",
  email: "",
  phoneCountryCode: "",
  phone: "",
  passportNumber: "",
  passportExpiry: null,
  passportIssueDate: null,
  passportLocation: "",
  specialDetails: "",
  expanded: expanded || isPrimary,
});

export const PAYMENT_METHODS = [
  { id: "card", label: "Credit / Debit Card" },
  { id: "net_banking", label: "Net Banking" },
  { id: "upi", label: "UPI" },
  { id: "wallet", label: "Wallet" },
];

export const getTripNights = (startDate, endDate, fallbackDuration) => {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    if (!Number.isNaN(diffMs) && diffMs > 0) {
      const nights = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (nights > 0) return nights;
    }
  }
  const durationMatch = fallbackDuration?.match(/(\d+)\s*Night/i);
  if (durationMatch?.[1]) {
    return Number(durationMatch[1]);
  }
  return 1;
};

export const calculateAddonAmount = (addon, travellers) => {
  if (!addon) return 0;
  const price = Number(addon.addon_price) || 0;
  const basis = (addon.pricing_basis || "per_passenger").toLowerCase();
  if (basis === "fixed") {
    return price;
  }
  return price * (travellers || 0);
};

export const calculateAccommodationAmount = (accommodation, nights, travelerCount = 0) => {
  if (!accommodation) return 0;
  return accommodation.addon.addon_price * Math.max(1, nights || 1) * travelerCount;
};


// -- Offer window (early bird / last minute / group deal) ---------------
export const getOfferWindow = (template) => {
  const now = new Date();
  if (template?.enable_early_bird_deal && template?.early_bird_start_date && template?.early_bird_end_date) {
    const start = new Date(template.early_bird_start_date);
    const formattedStart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(start);
    const end = new Date(template.early_bird_end_date);
    const formattedEnd = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(end);
    return { type: "early_bird", start, formattedStart, end, formattedEnd, isActive: now >= start && now <= end, isExpired: now > end, isUpcoming: now < start };
  }
  if (template?.enable_last_minute_deal && template?.last_minute_start_date && template?.last_minute_end_date) {
    const start = new Date(template.last_minute_start_date);
    const formattedStart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(start);
    const end = new Date(template.last_minute_end_date);
    const formattedEnd = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(end);
    return { type: "last_minute", start, formattedStart, end, formattedEnd, isActive: now >= start && now <= end, isExpired: now > end, isUpcoming: now < start };
  }
  if (template?.offer && template.offer !== "none") {
    return { type: template.offer, start: null, end: null, isActive: true, isExpired: false, isUpcoming: false };
  }
  return null;
};

// -- High Season Surcharge (HSS) window -----------------------
// `template` here is the resolved HSSTemplate object (fetched via /hss?ids=),
// NOT the departure row, the departure only carries the linked id + its own
// flat charge.
export const getHSSWindow = (template) => {
  if (!template?.valid_from || !template?.valid_until) return null;
  const now = new Date();
  const start = new Date(template.valid_from);
  const end = new Date(template.valid_until);
  return {
    start,
    end,
    isActive: now >= start && now <= end,
    isExpired: now > end,
    isUpcoming: now < start,
  };
};

// Live surcharge = (base price × template's percentage) + the departure's own
// flat charge, Returns 0 if the template's window isn't currently active
export const calculateHssSurcharge = (basePrice, template, departure) => {
  if (!template) return 0;
  const window = getHSSWindow(template);
  if (!window?.isActive) return 0;
  const percentage = Number(template.hss_percentage || 0);
  const flatCharge = Number(departure?.hss_flat_charge || 0);
  return (basePrice * percentage) / 100 + flatCharge;
};